import {
  Box,
  Button,
  Divider,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  Textarea,
  Tooltip,
} from '@chakra-ui/react';
import { Fragment, useCallback, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ReactComponent as InfoIcon } from 'assets/info-icon.svg';
import { Interface, FunctionFragment, ParamType } from 'ethers';
import { AccountId } from '@hashgraph/sdk';
import { createContractTransaction } from '../../api/multisigAPI';
import { useToast } from '../../hooks/useToast';
import { GlobalContext } from '../../providers/Global';
import { EXAMPLE_ABI, ModalType } from '../../utils/constants';
import { AbiMethodTypes } from 'types/abi';
import AbiInput from 'components/ABI';
import { ModalBackBtn } from './helpers';

// Helper to convert Hedera account ID to EVM address
const convertToEvmAddress = (value: string): string => {
  // If it's already an EVM address (starts with 0x), return as-is
  if (value.startsWith('0x')) {
    return value;
  }
  // If it looks like a Hedera account ID (e.g., 0.0.12345), convert it
  if (/^\d+\.\d+\.\d+$/.test(value)) {
    try {
      return '0x' + AccountId.fromString(value).toSolidityAddress();
    } catch (e) {
      console.error('Failed to convert Hedera account ID to EVM address:', e);
      return value;
    }
  }
  return value;
};

interface FunctionInput {
  name: string;
  type: string;
  value?: any;
}

interface ParsedFunction {
  name: string;
  inputs: FunctionInput[];
  fragment: FunctionFragment;
}

const ContractInteractionModal = () => {
  const {
    modals: {
      [`${ModalType.ContractInteractionModal}`]: { open },
    },
    closeModal,
    connection: { hashconnectConnectorInstance, accountId },
    connectedNetwork,
    currentAccount,
  } = useContext(GlobalContext);

  const [formatError, setFormatError] = useState(false);
  const [abi, setAbi] = useState('');
  const [isInvalid, setIsInvalid] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [contractFunctions, setContractFunctions] = useState<Record<string, ParsedFunction>>({});
  const [selectedMethodInputs, setSelectedMethodInputs] = useState<FunctionInput[]>([]);
  const [contractFunctionNames, setContractFunctionNames] = useState<string[]>([]);
  const [selectedMethod, setSelectedMethod] = useState('');
  const [contractId, setContractId] = useState('');
  const [gas, setGas] = useState('100000');
  const [payableAmount, setPayableAmount] = useState('0');
  const [ethersInterface, setEthersInterface] = useState<Interface | null>(null);
  const { showToast } = useToast();
  const { walletId } = useParams();

  const validateInputs = useCallback(() => {
    let invalid = false;
    if (!contractId || !selectedMethod || !gas) {
      invalid = true;
      setIsInvalid(true);
      return;
    }

    // Validate gas is a positive number
    const gasNum = parseInt(gas, 10);
    if (isNaN(gasNum) || gasNum <= 0) {
      invalid = true;
      setIsInvalid(true);
      return;
    }

    selectedMethodInputs.forEach((input: FunctionInput) => {
      if (input.type === AbiMethodTypes.Bool || input.type === 'bool') {
        if (typeof input.value !== 'boolean') {
          input.value = false;
        }
      } else if (input.value === undefined || input.value === '') {
        invalid = true;
      }
    });
    setIsInvalid(invalid);
  }, [contractId, selectedMethodInputs, selectedMethod, gas]);

  useEffect(() => {
    validateInputs();
  }, [contractId, selectedMethodInputs, selectedMethod, gas, validateInputs]);

  /**
   * Map Solidity type to AbiMethodTypes for input component compatibility
   */
  const mapSolidityTypeToAbiType = (solidityType: string): string => {
    if (solidityType === 'address') return AbiMethodTypes.Address;
    if (solidityType === 'bool') return AbiMethodTypes.Bool;
    if (solidityType === 'string') return AbiMethodTypes.String;
    if (solidityType.startsWith('uint')) return AbiMethodTypes.Uint256;
    if (solidityType.startsWith('int')) return AbiMethodTypes.Int256;
    return solidityType;
  };

  /**
   * Parse ABI using ethers.js Interface
   */
  const parseAbiWithEthers = (abiJson: any[]) => {
    try {
      const iface = new Interface(abiJson);
      setEthersInterface(iface);

      const functions: Record<string, ParsedFunction> = {};
      const functionNames: string[] = [];

      // Iterate through fragments to get writable functions
      iface.forEachFunction((fragment) => {
        // Only include non-view, non-pure functions (state-changing)
        if (fragment.stateMutability !== 'view' && fragment.stateMutability !== 'pure') {
          const inputs: FunctionInput[] = fragment.inputs.map((input: ParamType) => ({
            name: input.name || 'unnamed',
            type: mapSolidityTypeToAbiType(input.type),
            value: undefined,
          }));

          functionNames.push(fragment.name);
          functions[fragment.name] = {
            name: fragment.name,
            inputs,
            fragment,
          };
        }
      });

      if (functionNames.length === 0) {
        setFormatError(true);
        showToast('No writable functions found in ABI', 'warning', undefined, 'top-right');
        return;
      }

      setContractFunctions(functions);
      setContractFunctionNames(functionNames);
      setSelectedMethod(functionNames[0]);
      setSelectedMethodInputs([...functions[functionNames[0]].inputs]);
      setFormatError(false);
    } catch (e) {
      console.error('Error parsing ABI:', e);
      setFormatError(true);
      setEthersInterface(null);
      setContractFunctions({});
      setContractFunctionNames([]);
    }
  };

  /**
   * Encode function call data using ethers.js
   */
  const encodeFunctionData = (): Uint8Array | null => {
    if (!ethersInterface || !selectedMethod) return null;

    try {
      const func = contractFunctions[selectedMethod];
      if (!func) return null;

      // Prepare values in order
      const values = func.inputs.map((input: FunctionInput) => {
        // Handle type conversion
        if (input.type === AbiMethodTypes.Bool || input.type === 'bool') {
          return Boolean(input.value);
        }
        if (input.type.includes('uint') || input.type.includes('int')) {
          return input.value.toString();
        }
        // Convert Hedera account IDs to EVM addresses for address types
        if (input.type === 'address' || input.type === AbiMethodTypes.Address) {
          return convertToEvmAddress(input.value);
        }
        return input.value;
      });

      // Use ethers.Interface.encodeFunctionData
      const encodedData = ethersInterface.encodeFunctionData(selectedMethod, values);

      // Convert hex string to Uint8Array (remove 0x prefix)
      const hexString = encodedData.slice(2);
      const bytes = new Uint8Array(hexString.length / 2);
      for (let i = 0; i < hexString.length; i += 2) {
        bytes[i / 2] = parseInt(hexString.slice(i, i + 2), 16);
      }

      return bytes;
    } catch (e) {
      console.error('Error encoding function data:', e);
      return null;
    }
  };

  const handleCreate = async () => {
    if (!hashconnectConnectorInstance || !accountId || !connectedNetwork) {
      showToast('Wallet not connected', 'error', undefined, 'top-right');
      return;
    }

    try {
      setIsLoading(true);

      // Encode the function call using ethers.js
      const functionData = encodeFunctionData();
      if (!functionData) {
        showToast('Failed to encode function data', 'error', undefined, 'top-right');
        return;
      }

      // Use the multisig account as the payer
      const payerAccountId = currentAccount?.account_id?.toString() || walletId || '';

      // Create scheduled contract execution transaction
      const result = await hashconnectConnectorInstance.createContractExecuteScheduledTransaction(
        contractId,
        functionData,
        parseInt(gas, 10),
        parseFloat(payableAmount) || 0,
        payerAccountId,
        connectedNetwork,
        accountId
      );

      // Store the transaction in the backend for tracking
      const contractInteractionData = {
        contractId,
        contractMethod: selectedMethod,
        params: selectedMethodInputs.map((input: FunctionInput) => ({
          type: input.type,
          value: input.value,
        })),
        scheduleId: result.scheduleId,
        txId: result.txId,
        gas: parseInt(gas, 10),
        payableAmount: parseFloat(payableAmount) || 0,
      };

      await createContractTransaction(walletId || '', contractInteractionData);

      showToast('Contract interaction scheduled successfully', 'success', undefined, 'top-right');
      handleClose();
      window.location.reload();
    } catch (err: unknown) {
      console.error('Contract interaction error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to create contract interaction';
      showToast(errorMessage, 'error', undefined, 'top-right');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    closeModal(ModalType.ContractInteractionModal);
  };

  const handleDataChange = (index: number, value: any) => {
    const updatedData = [...selectedMethodInputs];
    updatedData[index].value = value;
    setSelectedMethodInputs(updatedData);
  };

  const handleInputChange = (inputValue: string) => {
    try {
      const parsedAbi = JSON.parse(inputValue);
      parseAbiWithEthers(parsedAbi);
    } catch (e) {
      setFormatError(true);
      setEthersInterface(null);
      setContractFunctions({});
      setContractFunctionNames([]);
    }
  };

  const handleMethodChange = (methodName: string) => {
    setSelectedMethod(methodName);
    const func = contractFunctions[methodName];
    if (func) {
      // Create fresh copies of inputs with reset values
      setSelectedMethodInputs(func.inputs.map(input => ({ ...input, value: undefined })));
    }
  };

  return (
    <>
      <Modal onClose={handleClose} isOpen={open} isCentered>
        <ModalOverlay />
        <ModalContent minWidth="586px">
          <ModalHeader pt={6} px={6} pb={0}>
            <ModalBackBtn />
            <Flex alignItems="center" flexDirection="column">
              <Text mt={10} size="2xl" variant="bodyBold">
                Contract Interaction
              </Text>
            </Flex>
            <ModalCloseButton
              mt={2}
              mr={2}
              color="#565656"
              _hover={{ border: 'none' }}
              _focusVisible={{ outline: 'none' }}
            />
          </ModalHeader>
          <ModalBody pb={14} pt={4} px={20}>
            <Flex alignItems="center" flexDirection="column" gap={4}>
              <Box width="full">
                <Text mb={2}>Contract Address (Hedera ID)</Text>
                <Input
                  h={12}
                  size="sm"
                  borderRadius={16}
                  value={contractId}
                  placeholder="0.0.xxxxx"
                  onChange={event => setContractId(event.target.value)}
                />
              </Box>
              <Box width="full" >
                <Flex justifyContent={'space-between'}>
                  <Text mb={2} width={'fit-content'} >Contract ABI</Text>
                  <Tooltip
                    bg={'primary'}
                    placement='right'
                    borderRadius={12}
                    fontSize={10}
                    label={
                      <pre>
                        <Text textAlign={'center'} p={2} m={2} borderTopRadius={12} bg={'secondary'} fontSize={10}>
                          EXAMPLE ABI
                        </Text>
                        <Text color={'black'} m={5} fontSize={10}>
                          {EXAMPLE_ABI}
                        </Text>
                      </pre>}
                    children={<InfoIcon cursor={'pointer'} />}
                  />
                </Flex>
                <Textarea
                  size="sm"
                  value={abi}
                  borderRadius={16}
                  isInvalid={formatError}
                  rows={5}
                  sx={{
                    height: '120px',
                    minHeight: '80px',
                    maxHeight: '200px',
                    overflowY: 'auto !important',
                    resize: 'vertical',
                  }}
                  _focusVisible={{
                    borderColor: formatError ? 'red' : '#3182ce',
                    boxShadow: formatError ? '0 0 0 1px red' : '0 0 0 1px #3182ce',
                  }}
                  onChange={event => {
                    setAbi(event.target.value);
                    handleInputChange(event.target.value);
                  }}
                />
                {formatError && (
                  <Text color="red" size="sm" mt={1} ml={2}>
                    Invalid ABI data
                  </Text>
                )}
              </Box>

              {/* Gas Input */}
              <Box width="full">
                <Text mb={2}>Gas Limit</Text>
                <Input
                  h={12}
                  size="sm"
                  borderRadius={16}
                  value={gas}
                  type="number"
                  placeholder="100000"
                  onChange={event => setGas(event.target.value)}
                />
              </Box>

              {/* Payable Amount (for payable functions) */}
              <Box width="full">
                <Flex alignItems="center" gap={2}>
                  <Text mb={2}>Payable Amount (HBAR)</Text>
                  <Tooltip
                    label="Amount of HBAR to send with the contract call (for payable functions)"
                    placement="top"
                    borderRadius={8}
                  >
                    <InfoIcon cursor="pointer" style={{ marginBottom: '8px' }} />
                  </Tooltip>
                </Flex>
                <Input
                  h={12}
                  size="sm"
                  borderRadius={16}
                  value={payableAmount}
                  type="number"
                  step="0.01"
                  placeholder="0"
                  onChange={event => setPayableAmount(event.target.value)}
                />
              </Box>

              {contractFunctionNames.length > 0 && (
                <>
                  <Box width="full" mt={2}>
                    <Text mb={4}>Contract Methods</Text>
                    <Select
                      h={12}
                      borderRadius={16}
                      value={selectedMethod}
                      onChange={e => handleMethodChange(e.target.value)}
                    >
                      {contractFunctionNames?.map((functionName: string, index: number) => {
                        return (
                          <option key={index} value={functionName}>
                            {functionName}
                          </option>
                        );
                      })}
                    </Select>
                  </Box>
                  {selectedMethodInputs.length > 0 && (
                    <Fragment>
                      <Divider orientation="horizontal" mt={2} />
                      <Flex width="full" gap={3} flexDirection="column">
                        {selectedMethodInputs.map((input: FunctionInput, index: number) => {
                          const key = `${input.type}-${index}`;
                          return <AbiInput key={key} abiProps={{
                            selectedMethodInputs,
                            input,
                            index,
                            setter: handleDataChange
                          }} />
                        })}
                      </Flex>
                    </Fragment>
                  )}
                </>
              )}
              {/* <MultiSigWarning /> */}
              <Button
                w={203}
                mt={5}
                onClick={handleCreate}
                variant="primary"
                isLoading={isLoading}
                isDisabled={isInvalid}
              >
                Create
              </Button>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ContractInteractionModal;
